import cx from 'classnames'
import { motion, AnimatePresence } from 'framer-motion'
import {
  type BaseSyntheticEvent,
  type FormEvent,
  useCallback,
  useContext,
  useState,
} from 'react'
import { useForm } from 'react-hook-form'

import { type SanityProductVariantFragment } from '@data/sanity/queries/types/product'
import { fadeAnimation } from '@lib/animate'
import { type RequestForPriceFormValues } from '@lib/form'
import { LanguageContext } from '@lib/language-context'
import { requestProductPrice } from '@lib/shopify/product'
import { StringsContext } from '@lib/strings-context'

import Alert from '@components/alert'
import Button, { ButtonVariant } from '@components/buttons/button'
import InputField from '@components/input-field/with-form-register'
import SimplePortableText from '@components/simple-portable-text'
import TelInputField from '@components/tel-input-field'
import TextArea from '@components/textarea'

interface ProductPriceRequestFormProps {
  activeVariant: SanityProductVariantFragment
  className?: string
}

const ProductPriceRequestForm = ({
  activeVariant,
  className,
}: ProductPriceRequestFormProps) => {
  const { locale } = useContext(LanguageContext)
  const strings = useContext(StringsContext)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isError, setIsError] = useState(false)

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
    setValue,
    trigger,
  } = useForm<RequestForPriceFormValues>()
  const firstNameRegister = register('firstName', {
    required: strings.firstNameMissing,
  })
  const lastNameRegister = register('lastName', {
    required: strings.lastNameMissing,
  })
  const emailRegister = register('email', {
    required: strings.emailMissing,
    pattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
      message: strings.emailInvalid,
    },
  })
  register('phone', {
    required: strings.phoneNumberMissing,
  })
  const companyRegister = register('company')
  const messageRegister = register('message')

  const resetForm = useCallback(
    (event: FormEvent) => {
      event.preventDefault()

      reset()
      setIsError(false)
      setIsSuccess(false)
      setIsSubmitting(false)
    },
    [reset]
  )

  const onSubmit = useCallback(
    async (
      formValues: RequestForPriceFormValues,
      event?: BaseSyntheticEvent
    ) => {
      event?.preventDefault()

      setIsError(false)
      setIsSuccess(false)

      setIsSubmitting(true)

      try {
        await requestProductPrice(locale, activeVariant.variantID, formValues)

        setIsSuccess(true)
      } catch (error) {
        console.log(error)
        setIsError(true)
      }

      setIsSubmitting(false)
    },
    [activeVariant, locale]
  )

  return (
    <div className={cx('flex flex-col gap-y-8', className)}>
      <h2 className="is-h3">{strings.priceRequestFormTitle}</h2>

      <form onSubmit={handleSubmit(onSubmit)}>
        <AnimatePresence mode="wait">
          {!isError && !isSuccess && (
            <motion.div
              initial="hide"
              animate="show"
              exit="hide"
              variants={fadeAnimation}
            >
              <div className="flex flex-col sm:flex-row sm:gap-4">
                <InputField
                  type="text"
                  formRegister={firstNameRegister}
                  label={strings.firstName}
                  placeholder={strings.firstNamePlaceholder}
                  errorMessage={errors.firstName?.message}
                  className="sm:w-1/2 mb-4"
                />

                <InputField
                  type="text"
                  formRegister={lastNameRegister}
                  label={strings.lastName}
                  placeholder={strings.lastNamePlaceholder}
                  errorMessage={errors.lastName?.message}
                  className="sm:w-1/2 mb-4"
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:gap-4">
                <InputField
                  type="email"
                  formRegister={emailRegister}
                  label={strings.emailAddress}
                  placeholder={strings.emailAddressPlaceholder}
                  errorMessage={errors.email?.message}
                  className="sm:w-1/2 mb-4"
                />

                <TelInputField
                  name="phone"
                  label={strings.phoneNumber}
                  placeholder={strings.phoneNumberPlaceholder}
                  errorMessage={errors.phone?.message}
                  onChange={(value: string) => {
                    setValue('phone', value)
                    trigger('phone')
                  }}
                  className="sm:w-1/2 mb-4"
                />
              </div>

              <InputField
                type="text"
                formRegister={companyRegister}
                label={strings.company}
                placeholder={strings.companyPlaceholder}
                errorMessage={errors.company?.message}
                className="mb-4"
              />

              <TextArea
                formRegister={messageRegister}
                label={strings.message}
                placeholder={strings.messagePlaceholder}
                errorMessage={errors.message?.message}
                rows={6}
                className="mb-4"
              />

              <div className="flex justify-center">
                <Button
                  type="submit"
                  variant={ButtonVariant.FILLED}
                  disabled={isSubmitting}
                  className="w-full mt-3"
                >
                  {strings.priceRequestFormSubmit}
                </Button>
              </div>
            </motion.div>
          )}

          {isSuccess && (
            <motion.div
              key="success"
              initial="hide"
              animate="show"
              exit="hide"
              variants={fadeAnimation}
            >
              <Alert>
                <SimplePortableText
                  content={strings.priceRequestFormSuccess}
                  className="rc rc-alert"
                />
              </Alert>
            </motion.div>
          )}

          {isError && (
            <motion.div
              key="error"
              initial="hide"
              animate="show"
              exit="hide"
              variants={fadeAnimation}
            >
              <Alert buttonText={strings.buttonTryAgain} onClick={resetForm}>
                <SimplePortableText
                  content={strings.priceRequestFormError}
                  className="rc rc-alert"
                />
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>
      </form>
    </div>
  )
}

export default ProductPriceRequestForm
